import React, { useState } from 'react';
import {
  Box, 
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Divider,
  Button,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  FolderOutlined as FolderIcon,
  LibraryBooksOutlined as LibraryBooksIcon,
  HistoryOutlined as HistoryIcon,
  MenuOutlined as MenuIcon,
  CloseOutlined as CloseIcon,
  PeopleOutlined as CommunityIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const DRAWER_WIDTH = 300;

const MaestroSideTray = ({ open, onClose, onNewChat }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Function to check if a navigation item is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname.startsWith('/lesson-plan');
    }
    return location.pathname.startsWith(path);
  };

  // Mock data - replace with actual data from your state management
  const recentLessonPlans = [
    { id: "2", title: "Unit 5.0 - Day 2", subject: "Functions", date: "Yesterday" },
    { id: "1", title: "Unit 5.0 - Day 1", subject: "Function Basics", date: "2 days ago" },
    { id: "5", title: "Unit 5.0 - Day 5", subject: "Advanced Functions", date: "Last week" },
    { id: "4", title: "Unit 5.0 - Day 4", subject: "Function Applications", date: "Last week" },
  ];


  const handleNewChat = () => {
    if (onNewChat) {
      onNewChat();
    } else {
      // TODO: Eventually navigate to new lesson plan creation
      navigate('/');
    }
    if (isMobile) {
      onClose();
    }
  };

  const handleLessonPlanClick = (lessonPlanId) => {
    // Navigate to specific lesson plan
    navigate(`/lesson-plan/${lessonPlanId}`);
    if (isMobile) {
      onClose();
    }
  };


  const drawerContent = (
    <Box sx={{ 
      width: DRAWER_WIDTH, 
      height: '100%', 
      bgcolor: theme.palette.background.paper,
      display: 'flex',
      flexDirection: 'column',
      borderRadius: "20px",
      boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[8],
      border: "none",
      m: 1,
      overflow: 'hidden'
    }}>
      {/* Header with New Lesson Plan button */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          {isMobile && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon sx={{ color: `${theme.palette.icon.primary}` }} />
            </IconButton>
          )}
        </Box>
        
        <Button variant="contained" size="shrinked" onClick={handleNewChat} sx={{ width: '100%' }}>
          <AddIcon sx={{ mr: 1 }} />
          New Lesson Plan
        </Button>
      </Box>

      {/* Navigation Items */}
      <List sx={{ flexGrow: 1, px: 1, overflow: 'auto' }}>
        {/* Lesson Plans Section */}
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => {
              navigate('/');
              if (isMobile) {
                onClose();
              }
            }}
            sx={{ 
              borderRadius: '8px', 
              mb: 1,
              bgcolor: isActive('/') ? theme.palette.action.selected : 'transparent',
              '&:hover': {
                bgcolor: isActive('/') ? theme.palette.action.selected : theme.palette.action.hover
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <FolderIcon sx={{ 
                color: isActive('/') ? theme.palette.primary.main : theme.palette.icon.primary 
              }} />
            </ListItemIcon>
            <ListItemText 
              primary="Lesson Plans" 
              primaryTypographyProps={{
                sx: { 
                  fontWeight: isActive('/') ? 600 : 400,
                  color: isActive('/') ? theme.palette.primary.main : 'inherit'
                }
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Resources Section */}
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => {
              navigate('/resources');
              if (isMobile) {
                onClose();
              }
            }}
            sx={{ 
              borderRadius: '8px', 
              mb: 1,
              bgcolor: isActive('/resources') ? theme.palette.action.selected : 'transparent',
              '&:hover': {
                bgcolor: isActive('/resources') ? theme.palette.action.selected : theme.palette.action.hover
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LibraryBooksIcon sx={{ 
                color: isActive('/resources') ? theme.palette.primary.main : theme.palette.icon.primary 
              }} />
            </ListItemIcon>
            <ListItemText 
              primary="Resources" 
              primaryTypographyProps={{
                sx: { 
                  fontWeight: isActive('/resources') ? 600 : 400,
                  color: isActive('/resources') ? theme.palette.primary.main : 'inherit'
                }
              }}
            />
          </ListItemButton>
        </ListItem>

        {/* Community Section */}
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => {
              navigate('/community');
              if (isMobile) {
                onClose();
              }
            }}
            sx={{ 
              borderRadius: '8px', 
              mb: 1,
              bgcolor: isActive('/community') ? theme.palette.action.selected : 'transparent',
              '&:hover': {
                bgcolor: isActive('/community') ? theme.palette.action.selected : theme.palette.action.hover
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <CommunityIcon sx={{ 
                color: isActive('/community') ? theme.palette.primary.main : theme.palette.icon.primary 
              }} />
            </ListItemIcon>
            <ListItemText 
              primary="Community" 
              primaryTypographyProps={{
                sx: { 
                  fontWeight: isActive('/community') ? 600 : 400,
                  color: isActive('/community') ? theme.palette.primary.main : 'inherit'
                }
              }}
            />
          </ListItemButton>
        </ListItem>

        <Divider sx={{ my: 2 }} />

        {/* Recents Section */}
        <ListItem disablePadding>
          <ListItemText 
            primary="Recents" 
            primaryTypographyProps={{ 
              variant: 'subtitle2', 
              color: 'text.secondary',
              sx: { pl: 1.5, fontWeight: 600 }
            }} 
          />
        </ListItem>

        {recentLessonPlans.map((lesson) => {
          const isLessonActive = location.pathname === `/lesson-plan/${lesson.id}`;
          
          return (
            <ListItem key={lesson.id} disablePadding>
              <ListItemButton 
                onClick={() => handleLessonPlanClick(lesson.id)}
                sx={{ 
                  borderRadius: '8px',
                  py: 1,
                  bgcolor: isLessonActive ? theme.palette.action.selected : 'transparent',
                  '&:hover': { 
                    bgcolor: isLessonActive ? theme.palette.action.selected : theme.palette.action.hover 
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <FolderIcon 
                    fontSize="small" 
                    sx={{ 
                      color: isLessonActive ? theme.palette.primary.main : theme.palette.icon.primary 
                    }} 
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={lesson.title}
                  secondary={lesson.subject + " • " + lesson.date}
                  primaryTypographyProps={{ 
                    variant: 'body2', 
                    noWrap: true,
                    sx: { 
                      fontWeight: isLessonActive ? 600 : 400,
                      color: isLessonActive ? theme.palette.primary.main : 'inherit'
                    }
                  }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Desktop: Always visible
  return (
    <Box
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        overflow: 'hidden',
      }}
    >
      {open && (
        <Box
          sx={{
            width: DRAWER_WIDTH,
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: theme.zIndex.drawer,
          }}
        >
          {drawerContent}
        </Box>
      )}
    </Box>
  );
};

export default MaestroSideTray;